import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Box, Typography, Tooltip, Switch } from "@mui/material";
import { AnchorLink } from "./StyledLink";

const CONTROL_SLOT_H = 48;

type TSwitchRowProps<TKey extends string, TItem> = {
  items: readonly TItem[];
  getKey: (i: TItem) => TKey;
  getLabel: (i: TItem) => string;
  state: Record<TKey, boolean>;
  onChange: (key: TKey, checked: boolean) => void;
  minWidth?: number;
  hideLabels?: boolean;
  lockedMap?: Partial<Record<TKey, boolean>>;
  disabledMap?: Partial<Record<TKey, boolean>>;
};

function SwitchRow<TKey extends string, TItem>({
  items,
  getKey,
  getLabel,
  state,
  onChange,
  minWidth = 150,
  hideLabels,
  lockedMap,
  disabledMap,
}: TSwitchRowProps<TKey, TItem>) {
  return (
    <Box
      sx={{
        display: "grid",
        gridAutoFlow: "column",
        gridAutoColumns: `${minWidth}px`,
        alignItems: hideLabels ? "center" : "end",
        pb: hideLabels ? 0 : 1,
      }}
    >
      {items.map((it) => {
        const key = getKey(it);
        const label = getLabel(it);
        const locked = !!lockedMap?.[key];
        const disabled = !!disabledMap?.[key];

        return (
          <Box
            key={key}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minWidth,
              textAlign: "center",
            }}
          >
            {!hideLabels && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 1, whiteSpace: "pre-line" }}
              >
                {label}
              </Typography>
            )}

            <Box
              sx={{
                height: CONTROL_SLOT_H,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {locked ? (
                <Tooltip
                  placement="top"
                  disableInteractive={false}
                  slotProps={{
                    tooltip: {
                      sx: (t) => ({
                        bgcolor: t.palette.background.paper,
                        color: t.palette.text.primary,
                        boxShadow: t.shadows[3],
                        border: `1px solid ${t.palette.divider}`,
                        p: 3,
                      }),
                    },
                    arrow: {
                      sx: (t) => ({ color: t.palette.background.paper }),
                    },
                  }}
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        Недоступно для управления.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        Доступность определяется в{" "}
                        <AnchorLink href="/system/manage/service_tools">
                          Справочнике Service Tools
                        </AnchorLink>
                        .
                      </Typography>
                    </Box>
                  }
                >
                  <WarningAmberOutlinedIcon
                    sx={{ color: "warning.main" }}
                    fontSize="small"
                  />
                </Tooltip>
              ) : (
                <Switch
                  checked={!!state[key]}
                  disabled={disabled}
                  onChange={(_, c) => onChange(key, c)}
                  inputProps={{ "aria-label": label }}
                />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default SwitchRow;
